import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ArrowLeft, Check, RefreshCw, Trash2, X, Loader2 } from 'lucide-react';
import { FormattedNote } from '../../../hooks/queries/noteTypes';
import { cn } from '../../../lib/utils';
import { supabaseService } from '../../../lib/supabase';
import { logger } from '../../../utils/logger';
import { useUser } from '../../../hooks/queries/useAuth';
import { getUserDisplayName } from '../../../lib/user-utils';
import { cacheUser, getCachedUser, getDisplayName } from '../../../utils/userCache';
// We'll use this later for rendered content view
// import { MarkdownContent } from '../../shared/ContentStyles';
import debounce from 'lodash.debounce';
import { MarkdownEditor } from './MarkdownEditor';
import { ErrorBoundary } from '../../ErrorBoundary';

interface NoteDetailPanelProps {
  note: FormattedNote;
  onClose: () => void;
  onEdit: (noteId: string, content: string, summary?: string | null) => Promise<void>;
  onDelete: (noteId: string) => Promise<void>;
  project?: {
    isShared?: boolean;
  };
  visible: boolean;
  onEditingStateChange?: (isEditing: boolean) => void;
}

export function NoteDetailPanel({
  note,
  onClose,
  onEdit,
  onDelete,
  project,
  visible,
  onEditingStateChange,
}: NoteDetailPanelProps) {
  // Single source of truth for note content
  const [currentNote, setCurrentNote] = useState(note);
  const [editInput, setEditInput] = useState(note.content || '');
  const [isDeleting, setIsDeleting] = useState(false);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [summaryInput, setSummaryInput] = useState<string | null>(note.summary);
  const [originalContent, setOriginalContent] = useState(note.content || '');
  const [saveStatus, setSaveStatus] = useState<'saving' | 'saved' | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const { data: user } = useUser();

  // Notify parent component about editing state changes
  useEffect(() => {
    onEditingStateChange?.(isEditing);
  }, [isEditing, onEditingStateChange]);

  // Handle save status changes and clearing
  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;

    // When saveStatus becomes 'saved', set a timeout to clear it
    if (saveStatus === 'saved') {
      timeout = setTimeout(() => {
        setSaveStatus(null);
      }, 3000);
    }

    // Cleanup timeout when component unmounts or saveStatus changes
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [saveStatus]);

  // Simple initialization when a note is selected
  useEffect(() => {
    logger.component('Note being loaded', {
      noteId: note.id,
      contentType: typeof note.content,
      contentLength: note.content?.length || 0,
      summary: note.summary,
    });

    // Reset UI state
    setIsDeleting(false);
    setIsGeneratingSummary(false);
    setIsEditing(false);

    // Process note content
    const contentToUse = note.content != null ? String(note.content) : '';

    // Update state in one go without delays
    setCurrentNote(note);
    setEditInput(contentToUse);
    setOriginalContent(contentToUse);
    setSummaryInput(note.summary);
    setSaveStatus(null);

    logger.component('Note loaded successfully', {
      noteId: note.id,
      contentLength: contentToUse.length,
      summaryLength: note.summary?.length || 0,
    });
  }, [note.id, note.content, note.summary]); // Depend on the specific properties we use

  // Create a debounced save function for content changes
  // Wait until typing has completely stopped for 1.5 seconds before saving
  const debouncedSave = useCallback(
    debounce(async (content: string, summary: string | null) => {
      // Only log when actually saving, not on every keystroke
      if (content !== note.content) {
        logger.component('Saving note after typing stopped', {
          noteId: note.id,
          contentLength: content.length,
        });
      }

      // Set saving status
      setSaveStatus('saving');

      try {
        // Save only after user has completely stopped typing
        const updatedNote = await onEdit(note.id, content, summary);

        if (updatedNote) {
          // If we received the updated note, use that data
          setCurrentNote(updatedNote);

          // Also update the summary field if it was changed server-side
          if (updatedNote.summary !== summaryInput) {
            setSummaryInput(updatedNote.summary);
          }
        } else {
          // Fallback if no note returned
          setCurrentNote(prev => ({
            ...prev,
            content: content,
          }));
        }

        // Set saved status and immediately mark as not editing
        setSaveStatus('saved');
        setIsEditing(false);
      } catch (error) {
        logger.error('Failed to save content', error);
        setSaveStatus(null);
      }
    }, 1500), // Wait 1.5 seconds after typing stops before saving
    [note.id, onEdit] // Remove summaryInput from deps to prevent debounce reset
  );

  // Create a debounced save function for summary changes
  // Consistent debounce time with content edits
  const debouncedSaveSummary = useCallback(
    debounce(async (summary: string | null, content: string) => {
      logger.component('Note summary save triggered after inactivity', {
        noteId: note.id,
        summaryPreview: summary?.substring(0, 20) + (summary && summary.length > 20 ? '...' : ''),
        hasChanged: summary !== note.summary,
      });

      // Set saving status
      setSaveStatus('saving');

      try {
        // Save only after user has completely stopped typing
        logger.component('Saving note summary to database after typing stopped');
        const updatedNote = await onEdit(note.id, content, summary);
        logger.component('Note summary save completed', {
          result: updatedNote?.summary,
          originalSummary: summary,
        });

        if (updatedNote) {
          // If we received the updated note, use that data
          setCurrentNote(updatedNote);
          // Make sure the input field is in sync
          setSummaryInput(updatedNote.summary);
        }

        // Set saved status and immediately mark as not editing
        setSaveStatus('saved');
        setIsEditing(false);
      } catch (error) {
        logger.error('Failed to save summary', error);
        setSaveStatus(null);
      }
    }, 1500), // Wait 1.5 seconds after typing stops before saving summary
    [note.id, onEdit] // Remove editInput from deps to prevent debounce reset
  );

  // Save changes and cleanup debounce on unmount
  useEffect(() => {
    return () => {
      // Flush pending saves before canceling to ensure changes are saved
      // This is crucial when the panel is closed before the debounce timer completes
      debouncedSave.flush();
      debouncedSaveSummary.flush();

      // Then cancel the debounced functions
      debouncedSave.cancel();
      debouncedSaveSummary.cancel();
    };
  }, [debouncedSave, debouncedSaveSummary]);

  // Handle input changes and trigger debounced save
  const handleInputChange = (newValue: string) => {
    // Mark as editing but don't show saving status yet (wait for debounce)
    // This prevents UI jumps while actively typing
    if (!isEditing) {
      setIsEditing(true);
    }

    // Only log significant changes, not every keystroke
    // This will be logged when the debounced save actually triggers

    // Calculate content change percentage
    const calculateChangePercentage = () => {
      if (!originalContent && !newValue) return 0;
      if (!originalContent) return 100;

      // If the content length has changed dramatically, consider it a major change
      const lengthRatio = Math.max(
        originalContent.length / Math.max(newValue.length, 1),
        newValue.length / Math.max(originalContent.length, 1)
      );

      if (lengthRatio > 2) {
        // Content length has doubled or halved
        return 95;
      }

      // Simple word count comparison for better accuracy
      const originalWords = originalContent.trim().split(/\s+/).filter(Boolean);
      const newWords = newValue.trim().split(/\s+/).filter(Boolean);

      // If either had no words, check if the other has words
      if (originalWords.length === 0 && newWords.length === 0) return 0;
      if (originalWords.length === 0 || newWords.length === 0) return 100;

      // Calculate word count difference as percentage
      const totalWords = Math.max(originalWords.length, newWords.length);
      const wordCountDiff = Math.abs(originalWords.length - newWords.length);
      const changePercentage = (wordCountDiff / totalWords) * 100;

      return Math.min(changePercentage, 100);
    };

    calculateChangePercentage();

    // Update state and trigger debounced save
    // The "saving" status will only appear after debounce timer expires
    setEditInput(newValue);
    debouncedSave(newValue, summaryInput);
  };

  // Handle summary input changes
  const handleSummaryChange = (newValue: string) => {
    // Mark as editing but don't show saving status yet (wait for debounce)
    // This prevents UI jumps while actively typing in the summary field
    if (!isEditing) {
      setIsEditing(true);
    }

    logger.component('Note summary changed - noteId', { noteId: note.id });
    setSummaryInput(newValue);
    debouncedSaveSummary(newValue, editInput);
  };

  // Fetch user details for shared projects or when needed
  useEffect(() => {
    // Skip when we already have the current user
    if (note.userId === 'current-user') {
      setDisplayName(getUserDisplayName(user) || 'You');
      return;
    }

    const loadUserDetails = async () => {
      try {
        // First check if this is the current user's note
        if (note.userId === user?.id) {
          // If it's our own note, use the current user's display name
          setDisplayName(getUserDisplayName(user) || 'You');
          return;
        }

        // Check if the user is in the cache - should be there if collaborators were preloaded
        const cachedUser = getCachedUser(note.userId);

        if (cachedUser) {
          // Use the cached user data
          logger.store('Using cached user data for note detail', { userId: note.userId });
          setDisplayName(getDisplayName(cachedUser));
        } else {
          // If not in cache, need to fetch user data
          // This should rarely happen if collaborators are preloaded correctly
          logger.fetch('No cached user data for note detail, fetching from service', {
            userId: note.userId,
          });

          // Fetch from service
          const [profile] = await supabaseService.fetchUserDetails([note.userId]);

          if (profile) {
            // Cache the user for future use
            cacheUser(note.userId, {
              id: note.userId,
              email: profile.email || '',
              display_name: profile.display_name || '',
              avatar_url: profile.avatar_url,
            });

            setDisplayName(
              getDisplayName({
                id: note.userId,
                email: profile.email || '',
                display_name: profile.display_name || '',
              })
            );
          } else {
            setDisplayName('User');
          }
        }
      } catch (error) {
        logger.error('Failed to load user details', error);
        setDisplayName(getUserDisplayName(user) || 'User');
      }
    };

    loadUserDetails();
  }, [note.userId, project?.isShared, user]);

  // Log when the note panel becomes visible
  useEffect(() => {
    if (visible) {
      logger.component('Notes detail panel visible', {
        noteId: note.id,
      });
    }
  }, [visible, note.id]);

  const date = new Date(note.createdAt);

  const handleDeleteConfirm = async () => {
    try {
      await onDelete(note.id);
      onClose();
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  };

  const handleRegenerateSummary = async () => {
    try {
      logger.component('Starting summary generation for note - noteId', { noteId: note.id });
      logger.component('Starting summary generation for note - userLanguage', {
        language: navigator.language || 'en-US',
      });

      setIsGeneratingSummary(true);

      // Get the user's language preference from their browser
      const userLanguage = navigator.language || 'en-US';

      // Use the note-summary Netlify function directly
      const response = await fetch('/.netlify/functions/note-summary', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          batchSize: 1,
          regenerateExisting: true,
          noteIds: [note.id],
          // Pass the language to ensure summary is in the same language as content
          language: userLanguage,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to generate summary: ${response.status} ${errorText}`);
      }

      const result = await response.json();
      logger.component('Summary generation response - noteId', { noteId: note.id });
      logger.component('Summary generation response - success', { success: result.success });
      logger.component('Summary generation response - processed', { processed: result.processed });
      logger.component('Summary generation response - successful', {
        successful: result.successful,
      });

      if (result.success) {
        // Wait a moment to ensure the summary has been saved to the database
        await new Promise(resolve => setTimeout(resolve, 500));

        // Get the updated note with the new summary
        const supabase = await supabaseService.getClient();
        const { data: updatedNote, error } = await supabase
          .from('notes')
          .select('*')
          .eq('id', note.id)
          .single();

        if (error) {
          logger.error('Error fetching updated note with summary', error);
          throw error;
        }

        // If successful, manually update the note object in this component
        if (updatedNote && updatedNote.summary) {
          logger.component('Successfully updated note with summary - noteId', { noteId: note.id });
          logger.component('Successfully updated note with summary - hasUpdatedNote', {
            hasUpdatedNote: !!updatedNote,
          });
          logger.component('Successfully updated note with summary - hasSummary', {
            hasSummary: !!updatedNote.summary,
          });
          logger.component('Successfully updated note with summary - summary', {
            summary: updatedNote.summary,
          });

          // Update the local state for immediate UI refresh
          setSummaryInput(updatedNote.summary);

          // Also update the parent component's state
          await onEdit(note.id, updatedNote.content, updatedNote.summary);
        } else {
          logger.error('Updated note missing summary', { updatedNote });
        }
      }
    } catch (error) {
      logger.error('Error regenerating summary', error);
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  return (
    <div
      className={cn(
        'fixed top-0 right-0 bottom-0 bg-transparent transition-all duration-500 ease-in-out flex flex-col z-50',
        visible ? 'transform-none delay-75' : 'translate-x-full',
        // On mobile, take full width
        'w-full left-0 lg:left-auto',
        // On desktop, match the width of the content area
        'lg:w-[calc(100%-560px)]'
      )}
      style={{ isolation: 'isolate' }}
    >
      {/* Single scrollable container that includes header and content */}
      <div className="flex-1 overflow-y-auto m-3 bg-white rounded-lg">
        {/* Header with back and action buttons - scrolls with content (no sticky positioning) */}
        <div className="relative p-4 flex items-center justify-between bg-white rounded-t-lg">
          {/* Save status indicator */}
          {saveStatus && (
            <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-2">
              {saveStatus === 'saving' ? (
                <>
                  <Loader2 size={14} className="animate-spin text-gray-500" />
                  <span className="text-xs text-gray-500">Saving...</span>
                </>
              ) : (
                <>
                  <Check size={14} className="text-green-500" />
                  <span className="text-xs text-green-500">Saved</span>
                </>
              )}
            </div>
          )}
          {isDeleting ? (
            <div className="w-full flex justify-between">
              <button
                onClick={() => setIsDeleting(false)}
                className="text-gray-500 hover:text-gray-700 flex items-center gap-1.5 transition-colors"
              >
                <X size={18} className="text-gray-600" />
                <span className="text-sm font-medium">Cancel</span>
              </button>
              <div></div>
              {/* Empty div for layout purposes */}
            </div>
          ) : (
            <>
              <button
                onClick={() => {
                  // No automatic summary generation - just close
                  onClose();
                }}
                className="text-gray-500 hover:text-gray-700 flex items-center gap-1.5 transition-colors"
              >
                <ArrowLeft size={20} className="text-gray-600" />
                <span className="text-sm font-medium">Back to notes</span>
              </button>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsDeleting(true)}
                  className="p-2 text-gray-400 hover:text-red-500 rounded-full hover:bg-red-50 transition-all"
                  title="Delete note"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </>
          )}
        </div>

        {/* Content area with padding */}
        <div className="py-4 px-10">
          <div className="max-w-3xl mx-auto">
            {isDeleting ? (
              <div className="space-y-6">
                {/* Delete Confirmation Dialog */}
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
                  <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4 overflow-hidden">
                    <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                      <div className="flex items-center text-red-600">
                        <AlertTriangle size={20} className="mr-2" />
                        <h3 className="font-bold">Delete Confirmation</h3>
                      </div>
                      <button
                        onClick={() => setIsDeleting(false)}
                        className="p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                      >
                        <X size={18} />
                      </button>
                    </div>
                    <div className="p-6">
                      <p className="text-gray-700 mb-6">
                        Are you sure you want to delete this note? This action cannot be undone.
                      </p>
                      <div className="flex justify-end space-x-4">
                        <button
                          onClick={() => setIsDeleting(false)}
                          className="px-4 py-2 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleDeleteConfirm}
                          className="px-4 py-2 rounded bg-red-500 hover:bg-red-600 text-white font-medium transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Note summary and metadata with cleaner design */}
                <div className="mb-6">
                  <div className="flex flex-col">
                    {/* Title area with editable Summary */}
                    <div className="w-full px-4">
                      <div className="relative">
                        <textarea
                          ref={el => {
                            if (el) {
                              el.style.height = 'auto';
                              el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
                            }
                          }}
                          value={summaryInput || ''}
                          onChange={e => {
                            handleSummaryChange(e.target.value);
                            // Auto-resize on change
                            e.target.style.height = 'auto';
                            e.target.style.height = `${Math.min(e.target.scrollHeight, 200)}px`;
                          }}
                          placeholder="Enter a summary..."
                          className="text-2xl font-bold text-gray-800 leading-tight w-full bg-transparent resize-none focus:outline-none focus:ring-0 border-0 py-1 min-h-[40px] overflow-hidden"
                          aria-label="Note summary"
                          rows={1}
                        />
                      </div>

                      {/* Meta info: Date, author, and save status on the same line */}
                      <div className="mt-2 flex items-center text-sm text-gray-500">
                        <span>
                          {date.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                        {displayName && (
                          <span className="flex items-center">
                            <span className="mx-1">•</span> {displayName}
                          </span>
                        )}

                        {/* Inline save status indicator - always present to prevent layout shifts */}
                        <span
                          className={`ml-3 text-sm z-10 relative flex items-center gap-1 min-w-[60px] ${
                            !saveStatus ? 'opacity-0' : 'opacity-100'
                          } transition-opacity duration-200`}
                        >
                          {saveStatus === 'saving' ? (
                            <span className="text-gray-500">Saving...</span>
                          ) : saveStatus === 'saved' ? (
                            <>
                              <Check size={12} className="text-green-500" />
                              <span className="text-gray-500">Saved</span>
                            </>
                          ) : (
                            <span className="invisible">Placeholder</span>
                          )}
                        </span>
                      </div>

                      {/* AI summary generation placed below with more discreet styling */}
                      {note.content && note.content.trim().length > 100 && (
                        <div className="mt-3">
                          <button
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              logger.component('Generate summary button clicked', {
                                noteId: note.id,
                                contentLength: note.content?.length || 0,
                                trimmedLength: note.content?.trim().length || 0,
                              });
                              void handleRegenerateSummary();
                            }}
                            disabled={isGeneratingSummary}
                            className={cn(
                              'text-xs font-medium transition-all flex items-center gap-1 text-gray-500 hover:text-gray-700',
                              isGeneratingSummary ? 'cursor-not-allowed' : ''
                            )}
                            title={summaryInput ? 'Generate new AI summary' : 'Generate AI summary'}
                          >
                            {isGeneratingSummary ? (
                              <>
                                <RefreshCw size={12} className="animate-spin" />
                                <span>Generating summary...</span>
                              </>
                            ) : (
                              <>
                                <svg
                                  className="h-3 w-3"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                  />
                                </svg>
                                <span>Generate summary</span>
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Note content - editable - aligned with title and metadata */}
                {/* Editor container with no padding to maintain alignment */}
                <div className="bg-transparent border-none px-0 relative">
                  <div className="p-0 editor-container">
                    {/* Markdown Editor with fallback to textarea if it fails to load */}
                    <ErrorBoundary
                      fallback={
                        <textarea
                          value={editInput}
                          onChange={e => handleInputChange(e.target.value)}
                          className="w-full h-full min-h-[300px] p-4 resize-none focus:outline-none focus:ring-0 border rounded-md"
                          placeholder="Start typing your note here..."
                        />
                      }
                    >
                      {/* Markdown editor with its own styling */}
                      <MarkdownEditor
                        key={`editor-${currentNote.id}`}
                        content={editInput}
                        onChange={handleInputChange}
                        placeholder="Start typing your note here..."
                        autoFocus={false} /* Don't auto-focus, users may want to read first */
                        topicId={
                          currentNote.topicId
                        } /* Pass the topic ID for proper storage paths */
                      />
                    </ErrorBoundary>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
